import React, { useContext, useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Context } from '../../../App';
import { formatValue } from './mohrCircleMath';

const MARGIN = { top: 16, right: 20, bottom: 40, left: 56 };
const VIEW_WIDTH = 760;
const VIEW_HEIGHT = 420;
const WORLD_PADDING_RATIO = 0.1;
const WORLD_PADDING_MIN = 4;
const TWO_PI = Math.PI * 2;
const FACE_LABEL_FONT_SIZE = 10.5;
const FACE_LABEL_DETAIL_FONT_SIZE = 10;
const FACE_LABEL_LINE_GAP = 11;
const FACE_LABEL_HORIZONTAL_PAD = 6;
const FACE_LABEL_VERTICAL_PAD = 5;
const FACE_LABEL_OFFSET = 18;
const FACE_LABEL_EDGE_PAD = 6;
const SUMMARY_TABLE_HEADER_HEIGHT = 18;
const SUMMARY_TABLE_ROW_HEIGHT = 18;
const SUMMARY_TABLE_MARGIN = 10;
const SUMMARY_TABLE_HORIZONTAL_PAD = 5;
const SUMMARY_TABLE_LABEL_VALUE_GAP = 8;
const SUMMARY_TABLE_LABEL_FONT_SIZE = 10;
const SUMMARY_TABLE_VALUE_FONT_SIZE = 10;
const SUMMARY_TABLE_HEADER_FONT_SIZE = 10.5;
const SUBSCRIPT_SCALE = 0.72;

const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const radToDeg = (rad) => (rad * 180) / Math.PI;
const estimateTextWidth = (text, fontSize = FACE_LABEL_FONT_SIZE) => text.length * fontSize * 0.56;
const estimatePartsWidth = (parts, fontSize = SUMMARY_TABLE_LABEL_FONT_SIZE) =>
  parts.reduce((sum, part) => sum + estimateTextWidth(part.text, part.subscript ? fontSize * SUBSCRIPT_SCALE : fontSize), 0);

const normalizeRad = (angle) => {
  let normalized = angle % TWO_PI;
  if (normalized < 0) normalized += TWO_PI;
  return normalized;
};

const smallestAngleDelta = (from, to) => {
  let delta = normalizeRad(to - from);
  if (delta > Math.PI) delta -= TWO_PI;
  return delta;
};

const buildArcPolylinePoints = (cx, cy, radius, startAngle, endAngle) => {
  const delta = smallestAngleDelta(startAngle, endAngle);
  const segmentCount = Math.max(8, Math.ceil(Math.abs(delta) / (Math.PI / 24)));
  const points = Array.from({ length: segmentCount + 1 }, (_, index) => {
    const t = index / segmentCount;
    const angle = startAngle + delta * t;
    const x = cx + radius * Math.cos(angle);
    const y = cy + radius * Math.sin(angle);
    return `${x},${y}`;
  });
  return points.join(' ');
};

const getArcMidpoint = (cx, cy, radius, startAngle, endAngle) => {
  const delta = smallestAngleDelta(startAngle, endAngle);
  const midAngle = startAngle + delta / 2;
  return {
    x: cx + radius * Math.cos(midAngle),
    y: cy + radius * Math.sin(midAngle),
    deltaDeg: Math.abs(radToDeg(delta)),
  };
};

const buildTicks = (min, max, count = 6) => {
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min || count < 2) return [min, max];
  const step = (max - min) / (count - 1);
  return Array.from({ length: count }, (_, idx) => min + idx * step);
};

const collectSigmaValues = (state) => {
  if (!state) return [];
  const values = [
    state.centerSigma,
    state.sigma1,
    state.sigma3,
    state.points?.xFace?.sigma,
    state.points?.yFace?.sigma,
    state.points?.selected?.sigma,
  ];
  return values.filter((value) => Number.isFinite(value));
};

const collectTauValues = (state) => {
  if (!state) return [];
  const values = [
    0,
    state.tauMax,
    -state.tauMax,
    state.points?.xFace?.tau,
    state.points?.yFace?.tau,
    state.points?.selected?.tau,
  ];
  return values.filter((value) => Number.isFinite(value));
};

const buildFaceLabelLayout = ({ point, center, plot, title, detail }) => {
  const dx = point.x - center.x;
  const dy = point.y - center.y;
  const distance = Math.hypot(dx, dy) || 1;
  const nx = dx / distance;
  const ny = dy / distance;
  const textAnchor = nx >= 0 ? 'start' : 'end';
  const width =
    Math.max(estimateTextWidth(title, FACE_LABEL_FONT_SIZE), estimateTextWidth(detail, FACE_LABEL_DETAIL_FONT_SIZE)) +
    FACE_LABEL_HORIZONTAL_PAD * 2;
  const height = FACE_LABEL_VERTICAL_PAD * 2 + FACE_LABEL_FONT_SIZE + FACE_LABEL_LINE_GAP;

  let boxX = point.x + nx * FACE_LABEL_OFFSET + (textAnchor === 'start' ? 6 : -width - 6);
  let boxY = point.y + ny * FACE_LABEL_OFFSET - height / 2;

  boxX = clamp(boxX, plot.left + FACE_LABEL_EDGE_PAD, plot.right - width - FACE_LABEL_EDGE_PAD);
  boxY = clamp(boxY, plot.top + FACE_LABEL_EDGE_PAD, plot.bottom - height - FACE_LABEL_EDGE_PAD);

  return {
    boxX,
    boxY,
    width,
    height,
    textAnchor,
    connectorX: textAnchor === 'start' ? boxX : boxX + width,
    connectorY: clamp(point.y + ny * FACE_LABEL_OFFSET, boxY + 7, boxY + height - 7),
    titleX: textAnchor === 'start' ? boxX + FACE_LABEL_HORIZONTAL_PAD : boxX + width - FACE_LABEL_HORIZONTAL_PAD,
    titleY: boxY + FACE_LABEL_VERTICAL_PAD + FACE_LABEL_FONT_SIZE - 1,
    detailX: textAnchor === 'start' ? boxX + FACE_LABEL_HORIZONTAL_PAD : boxX + width - FACE_LABEL_HORIZONTAL_PAD,
    detailY: boxY + FACE_LABEL_VERTICAL_PAD + FACE_LABEL_FONT_SIZE + FACE_LABEL_LINE_GAP,
  };
};

const SvgTextWithSubscript = ({ x, y, parts, fontSize = 11, fill = '#000', fontWeight = '700', textAnchor = 'start' }) => {
  const numericFontSize = typeof fontSize === 'number' ? fontSize : Number(fontSize);
  const subscriptFontSize = Number.isFinite(numericFontSize) ? Math.max(8, numericFontSize * SUBSCRIPT_SCALE) : undefined;

  return (
    <text x={x} y={y} fontSize={fontSize} fill={fill} fontWeight={fontWeight} textAnchor={textAnchor}>
      {parts.map((part, index) => (
        <tspan
          key={`${part.text}-${index}`}
          baselineShift={part.subscript ? 'sub' : 'baseline'}
          fontSize={part.subscript && subscriptFontSize ? subscriptFontSize : undefined}
        >
          {part.text}
        </tspan>
      ))}
    </text>
  );
};

const buildSketchColors = (theme) =>
  theme === 'dark'
    ? {
        canvasBackground: '#0f151b',
        plotBackground: '#151d26',
        plotBorder: '#33414f',
        gridPrimary: '#25303a',
        gridSecondary: '#202933',
        axis: '#7d8b97',
        axisText: '#d0dae2',
        mutedText: '#a7b4be',
        emptyText: '#97a5b1',
        circle: '#7cb7ff',
        center: '#7cb7ff',
        diameter: '#6fda86',
        selected: '#7fe08b',
        xFace: '#7cc7ff',
        xFaceProjection: '#9bb6ff',
        yFace: '#d8b59a',
        yFaceProjection: '#e3cdbd',
        principal: '#ff8a80',
        dimension: '#9fb0ba',
        dimensionText: '#c5d0d8',
        angleStroke: '#93a4ad',
        angleText: '#d9c7ba',
        calloutFill: '#1e2730',
        calloutFillOpacity: 0.98,
        calloutDetail: '#d2dde5',
        calloutBorderOpacity: 0.45,
        maxShear: '#c3ced6',
        summaryFill: '#182028',
        summaryFillOpacity: 0.98,
        summaryBorder: '#344350',
        summaryHeader: '#d5e0e8',
        summaryDivider: '#2c3944',
        summaryLabel: '#bcc9d3',
        summaryValue: '#f1f5f8',
        tick: '#8a99a5',
        tickText: '#aab7c1',
      }
    : {
        canvasBackground: '#fbfbfb',
        plotBackground: '#ffffff',
        plotBorder: '#d8d8d8',
        gridPrimary: '#f0f0f0',
        gridSecondary: '#f4f4f4',
        axis: '#9e9e9e',
        axisText: '#555555',
        mutedText: '#666666',
        emptyText: '#777777',
        circle: '#1565c0',
        center: '#1565c0',
        diameter: '#2e7d32',
        selected: '#2e7d32',
        xFace: '#1565c0',
        xFaceProjection: '#3949ab',
        yFace: '#8d6e63',
        yFaceProjection: '#6d4c41',
        principal: '#c62828',
        dimension: '#616161',
        dimensionText: '#555555',
        angleStroke: '#616161',
        angleText: '#4e342e',
        calloutFill: '#ffffff',
        calloutFillOpacity: 0.94,
        calloutDetail: '#37474f',
        calloutBorderOpacity: 0.35,
        maxShear: '#424242',
        summaryFill: '#ffffff',
        summaryFillOpacity: 0.95,
        summaryBorder: '#cfd8dc',
        summaryHeader: '#455a64',
        summaryDivider: '#e0e0e0',
        summaryLabel: '#455a64',
        summaryValue: '#263238',
        tick: '#808080',
        tickText: '#666666',
      };

const MohrCircleSketch = ({ state, title }) => {
  const { theme: appTheme } = useContext(Context);
  const colors = useMemo(() => buildSketchColors(appTheme), [appTheme]);
  const model = useMemo(() => {
    const plot = {
      left: MARGIN.left,
      right: VIEW_WIDTH - MARGIN.right,
      top: MARGIN.top,
      bottom: VIEW_HEIGHT - MARGIN.bottom,
    };
    plot.width = plot.right - plot.left;
    plot.height = plot.bottom - plot.top;

    if (!state) {
      return {
        plot,
        isEmpty: true,
      };
    }

    const sigmaValues = collectSigmaValues(state);
    const tauValues = collectTauValues(state);

    const sigmaMinRaw = Math.min(...sigmaValues);
    const sigmaMaxRaw = Math.max(...sigmaValues);
    const tauMinRaw = Math.min(...tauValues);
    const tauMaxRaw = Math.max(...tauValues);

    const sigmaSpan = Math.max(1e-6, sigmaMaxRaw - sigmaMinRaw);
    const tauSpan = Math.max(1e-6, tauMaxRaw - tauMinRaw);

    const baseSpan = Math.max(sigmaSpan, tauSpan);
    const uniformPadding = Math.max(WORLD_PADDING_MIN, baseSpan * WORLD_PADDING_RATIO);

    const world = {
      sigmaMin: sigmaMinRaw - uniformPadding,
      sigmaMax: sigmaMaxRaw + uniformPadding,
      tauMin: tauMinRaw - uniformPadding,
      tauMax: tauMaxRaw + uniformPadding,
    };

    const sigmaRange = Math.max(1e-6, world.sigmaMax - world.sigmaMin);
    const tauRange = Math.max(1e-6, world.tauMax - world.tauMin);
    const scale = Math.min(plot.width / sigmaRange, plot.height / tauRange);
    const fittedWidth = sigmaRange * scale;
    const fittedHeight = tauRange * scale;
    const drawBounds = {
      left: plot.left + (plot.width - fittedWidth) / 2,
      right: plot.left + (plot.width - fittedWidth) / 2 + fittedWidth,
      top: plot.top + (plot.height - fittedHeight) / 2,
      bottom: plot.top + (plot.height - fittedHeight) / 2 + fittedHeight,
    };

    const mapX = (sigma) => drawBounds.left + (sigma - world.sigmaMin) * scale;
    const mapY = (tau) => drawBounds.top + (tau - world.tauMin) * scale;

    const ticks = {
      sigma: buildTicks(world.sigmaMin, world.sigmaMax, 7),
      tau: buildTicks(world.tauMin, world.tauMax, 7),
    };

    const tauAxisY = clamp(mapY(0), drawBounds.top, drawBounds.bottom);
    const sigmaAxisX = clamp(mapX(0), drawBounds.left, drawBounds.right);

    const centerX = mapX(state.centerSigma);
    const centerY = mapY(0);
    const radiusPx = Math.abs(state.radius * scale);
    const xFaceAngle = Math.atan2(state.points.xFace.tau, state.points.xFace.sigma - state.centerSigma);
    const sigma1Angle = 0;
    const tauTopAngle = -Math.PI / 2;
    const tauBottomAngle = Math.PI / 2;
    const tauShearAngle =
      Math.abs(smallestAngleDelta(xFaceAngle, tauTopAngle)) <= Math.abs(smallestAngleDelta(xFaceAngle, tauBottomAngle))
        ? tauTopAngle
        : tauBottomAngle;

    const thetaPArcRadius = Math.max(16, radiusPx * 0.28);
    const thetaSArcRadius = Math.max(12, radiusPx * 0.18);
    const thetaPArcMid = getArcMidpoint(centerX, centerY, thetaPArcRadius, xFaceAngle, sigma1Angle);
    const thetaSArcMid = getArcMidpoint(centerX, centerY, thetaSArcRadius, xFaceAngle, tauShearAngle);

    const points = {
      xFace: {
        x: mapX(state.points.xFace.sigma),
        y: mapY(state.points.xFace.tau),
        title: 'x-face',
        detail: `(${formatValue(state.points.xFace.sigma, 1)}, ${formatValue(state.points.xFace.tau, 1)})`,
        color: colors.xFace,
      },
      yFace: {
        x: mapX(state.points.yFace.sigma),
        y: mapY(state.points.yFace.tau),
        title: 'y-face',
        detail: `(${formatValue(state.points.yFace.sigma, 1)}, ${formatValue(state.points.yFace.tau, 1)})`,
        color: colors.yFace,
      },
      selected: {
        x: mapX(state.points.selected.sigma),
        y: mapY(state.points.selected.tau),
        label: `Selected (${formatValue(state.points.selected.sigma, 1)}, ${formatValue(state.points.selected.tau, 1)})`,
        color: colors.selected,
      },
      principal1: {
        x: mapX(state.points.principal1.sigma),
        y: mapY(0),
        label: 'sigma1',
        color: colors.principal,
      },
      principal3: {
        x: mapX(state.points.principal3.sigma),
        y: mapY(0),
        label: 'sigma3',
        color: colors.principal,
      },
      maxShearPositive: {
        x: mapX(state.points.maxShearPositive.sigma),
        y: mapY(state.points.maxShearPositive.tau),
        label: 'tauMax',
      },
      maxShearNegative: {
        x: mapX(state.points.maxShearNegative.sigma),
        y: mapY(state.points.maxShearNegative.tau),
        label: '-tauMax',
      },
    };

    points.xFace.callout = buildFaceLabelLayout({
      point: points.xFace,
      center: { x: centerX, y: centerY },
      plot,
      title: points.xFace.title,
      detail: points.xFace.detail,
    });
    points.yFace.callout = buildFaceLabelLayout({
      point: points.yFace,
      center: { x: centerX, y: centerY },
      plot,
      title: points.yFace.title,
      detail: points.yFace.detail,
    });

    const summaryRows = [
      {
        id: 'thetaS',
        parts: [{ text: '2θ' }, { text: 's', subscript: true }],
        value: `${formatValue(thetaSArcMid.deltaDeg, 1)}°`,
      },
      {
        id: 'thetaP',
        parts: [{ text: '2θ' }, { text: 'p', subscript: true }],
        value: `${formatValue(thetaPArcMid.deltaDeg, 1)}°`,
      },
      {
        id: 'radius',
        parts: [{ text: 'R' }],
        value: `${formatValue(state.radius, 2)} kPa`,
      },
      {
        id: 'centerSigma',
        parts: [{ text: 'C = σ' }, { text: 'avg', subscript: true }],
        value: `${formatValue(state.centerSigma, 3)} kPa`,
      },
    ];
    const summaryTableWidth = Math.max(
      estimateTextWidth('Key Values', SUMMARY_TABLE_HEADER_FONT_SIZE) + SUMMARY_TABLE_HORIZONTAL_PAD * 2,
      ...summaryRows.map(
        (row) =>
          estimatePartsWidth(row.parts, SUMMARY_TABLE_LABEL_FONT_SIZE) +
          estimateTextWidth(row.value, SUMMARY_TABLE_VALUE_FONT_SIZE) +
          SUMMARY_TABLE_HORIZONTAL_PAD * 2 +
          SUMMARY_TABLE_LABEL_VALUE_GAP,
      ),
    );

    return {
      plot,
      drawBounds,
      isEmpty: false,
      mapX,
      mapY,
      ticks,
      tauAxisY,
      sigmaAxisX,
      center: { x: centerX, y: centerY },
      radiusPx,
      points,
      annotations: {
        tauXYDim: {
          x: plot.left + 14,
          y1: tauAxisY,
          y2: points.xFace.y,
          value: formatValue(state.points.xFace.tau, 2),
        },
        thetaP: {
          show: Math.abs(thetaPArcMid.deltaDeg) > 0.2,
          polylinePoints: buildArcPolylinePoints(centerX, centerY, thetaPArcRadius, xFaceAngle, sigma1Angle),
          text: {
            x: thetaPArcMid.x + 5,
            y: thetaPArcMid.y - 5,
            value: formatValue(thetaPArcMid.deltaDeg, 1),
          },
        },
        thetaS: {
          show: Math.abs(thetaSArcMid.deltaDeg) > 0.2,
          polylinePoints: buildArcPolylinePoints(centerX, centerY, thetaSArcRadius, xFaceAngle, tauShearAngle),
          text: {
            x: thetaSArcMid.x - 6,
            y: thetaSArcMid.y + 12,
            value: formatValue(thetaSArcMid.deltaDeg, 1),
          },
        },
        summaryTable: {
          x: plot.right - summaryTableWidth - SUMMARY_TABLE_MARGIN,
          y: plot.top + SUMMARY_TABLE_MARGIN,
          width: summaryTableWidth,
          rows: summaryRows,
        },
      },
    };
  }, [state, colors]);

  return (
    <Stack spacing={1.2}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, textAlign: 'left' }}>
        {title}
      </Typography>
      <Box className="sketch-rail" sx={{ px: 0 }}>
        <Box className="sketch-wrap">
          <svg
            viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
            role="img"
            aria-label={`${title} sketch`}
            style={{ borderRadius: 6, background: colors.canvasBackground }}
          >
            <rect
              x={model.plot.left}
              y={model.plot.top}
              width={model.plot.width}
              height={model.plot.height}
              fill={colors.plotBackground}
              stroke={colors.plotBorder}
            />

            {!model.isEmpty &&
              model.ticks.tau.map((tick) => (
                <line
                  key={`tau-grid-${tick}`}
                  x1={model.plot.left}
                  y1={model.mapY(tick)}
                  x2={model.plot.right}
                  y2={model.mapY(tick)}
                  stroke={colors.gridPrimary}
                  strokeWidth="1"
                />
              ))}
            {!model.isEmpty &&
              model.ticks.sigma.map((tick) => (
                <line
                  key={`sigma-grid-${tick}`}
                  x1={model.mapX(tick)}
                  y1={model.plot.top}
                  x2={model.mapX(tick)}
                  y2={model.plot.bottom}
                  stroke={colors.gridSecondary}
                  strokeWidth="1"
                />
              ))}

            {!model.isEmpty && (
              <>
                <line
                  x1={model.plot.left}
                  y1={model.tauAxisY}
                  x2={model.plot.right}
                  y2={model.tauAxisY}
                  stroke={colors.axis}
                  strokeWidth="1.2"
                />
                <line
                  x1={model.sigmaAxisX}
                  y1={model.plot.top}
                  x2={model.sigmaAxisX}
                  y2={model.plot.bottom}
                  stroke={colors.axis}
                  strokeWidth="1.2"
                />
                <line
                  x1={model.center.x}
                  y1={model.points.maxShearNegative.y}
                  x2={model.center.x}
                  y2={model.points.maxShearPositive.y}
                  stroke={colors.axis}
                  strokeWidth="1"
                  strokeDasharray="4 4"
                />
              </>
            )}

            {!model.isEmpty && (
              <>
                <circle
                  cx={model.center.x}
                  cy={model.center.y}
                  r={model.radiusPx}
                  fill="none"
                  stroke={colors.circle}
                  strokeWidth="2.2"
                />
                <line
                  x1={model.points.yFace.x}
                  y1={model.points.yFace.y}
                  x2={model.points.xFace.x}
                  y2={model.points.xFace.y}
                  stroke={colors.diameter}
                  strokeWidth="2"
                />
                <circle cx={model.center.x} cy={model.center.y} r="3.2" fill={colors.center} />
                <SvgTextWithSubscript
                  x={model.center.x + 8}
                  y={model.center.y - 8}
                  fontSize={11}
                  fill={colors.center}
                  parts={[{ text: 'C = σ' }, { text: 'avg', subscript: true }]}
                />
                <text
                  x={(model.center.x + model.points.xFace.x) / 2 + 6}
                  y={(model.center.y + model.points.xFace.y) / 2 - 6}
                  fontSize="11"
                  fill={colors.diameter}
                  fontWeight="700"
                >
                  R
                </text>
                <line
                  x1={model.center.x}
                  y1={model.center.y}
                  x2={model.points.selected.x}
                  y2={model.points.selected.y}
                  stroke={colors.selected}
                  strokeWidth="1.5"
                  strokeDasharray="5 3"
                />
                <line
                  x1={model.points.xFace.x}
                  y1={model.points.xFace.y}
                  x2={model.points.xFace.x}
                  y2={model.tauAxisY}
                  stroke={colors.xFaceProjection}
                  strokeWidth="1.2"
                  strokeDasharray="5 3"
                />
                <line
                  x1={model.points.yFace.x}
                  y1={model.points.yFace.y}
                  x2={model.points.yFace.x}
                  y2={model.tauAxisY}
                  stroke={colors.yFaceProjection}
                  strokeWidth="1.2"
                  strokeDasharray="5 3"
                />
                <SvgTextWithSubscript
                  x={model.points.xFace.x + 4}
                  y={model.tauAxisY - 8}
                  fontSize={11}
                  fill={colors.xFaceProjection}
                  parts={[{ text: 'σ' }, { text: 'x', subscript: true }]}
                />
                <SvgTextWithSubscript
                  x={model.points.yFace.x - 22}
                  y={model.tauAxisY + 18}
                  fontSize={11}
                  fill={colors.yFaceProjection}
                  parts={[{ text: 'σ' }, { text: 'y', subscript: true }]}
                />
                <line
                  x1={model.annotations.tauXYDim.x}
                  y1={model.annotations.tauXYDim.y1}
                  x2={model.annotations.tauXYDim.x}
                  y2={model.annotations.tauXYDim.y2}
                  stroke={colors.dimension}
                  strokeWidth="1.1"
                />
                <line
                  x1={model.annotations.tauXYDim.x - 5}
                  y1={model.annotations.tauXYDim.y1}
                  x2={model.annotations.tauXYDim.x + 5}
                  y2={model.annotations.tauXYDim.y1}
                  stroke={colors.dimension}
                  strokeWidth="1.1"
                />
                <line
                  x1={model.annotations.tauXYDim.x - 5}
                  y1={model.annotations.tauXYDim.y2}
                  x2={model.annotations.tauXYDim.x + 5}
                  y2={model.annotations.tauXYDim.y2}
                  stroke={colors.dimension}
                  strokeWidth="1.1"
                />
                <SvgTextWithSubscript
                  x={model.annotations.tauXYDim.x + 7}
                  y={(model.annotations.tauXYDim.y1 + model.annotations.tauXYDim.y2) / 2 + 4}
                  fontSize={10.5}
                  fill={colors.dimensionText}
                  fontWeight="400"
                  parts={[{ text: 'τ' }, { text: 'xy', subscript: true }, { text: `=${model.annotations.tauXYDim.value}` }]}
                />

                {model.annotations.thetaP.show && (
                  <>
                    <polyline
                      points={model.annotations.thetaP.polylinePoints}
                      fill="none"
                      stroke={colors.angleStroke}
                      strokeWidth="1.2"
                      strokeDasharray="3 2"
                    />
                    <SvgTextWithSubscript
                      x={model.annotations.thetaP.text.x}
                      y={model.annotations.thetaP.text.y}
                      fontSize={10}
                      fill={colors.angleText}
                      parts={[{ text: '2θ' }, { text: 'p', subscript: true }]}
                    />
                  </>
                )}

                {model.annotations.thetaS.show && (
                  <>
                    <polyline
                      points={model.annotations.thetaS.polylinePoints}
                      fill="none"
                      stroke={colors.angleStroke}
                      strokeWidth="1"
                      strokeDasharray="3 2"
                    />
                    <SvgTextWithSubscript
                      x={model.annotations.thetaS.text.x}
                      y={model.annotations.thetaS.text.y}
                      fontSize={10}
                      fill={colors.angleText}
                      parts={[{ text: '2θ' }, { text: 's', subscript: true }]}
                    />
                  </>
                )}
              </>
            )}

            {!model.isEmpty && (
              <>
                <g>
                  <circle
                    cx={model.points.principal1.x}
                    cy={model.points.principal1.y}
                    r="4.5"
                    fill={model.points.principal1.color}
                  />
                  <SvgTextWithSubscript
                    x={model.points.principal1.x + 7}
                    y={model.points.principal1.y - 7}
                    fontSize={11}
                    fill={model.points.principal1.color}
                    parts={[{ text: 'σ' }, { text: '1', subscript: true }]}
                  />
                </g>
                <g>
                  <circle
                    cx={model.points.principal3.x}
                    cy={model.points.principal3.y}
                    r="4.5"
                    fill={model.points.principal3.color}
                  />
                  <SvgTextWithSubscript
                    x={model.points.principal3.x + 7}
                    y={model.points.principal3.y - 7}
                    fontSize={11}
                    fill={model.points.principal3.color}
                    parts={[{ text: 'σ' }, { text: '3', subscript: true }]}
                  />
                </g>
                <g>
                  <circle cx={model.points.xFace.x} cy={model.points.xFace.y} r="4.2" fill={model.points.xFace.color} />
                  <line
                    x1={model.points.xFace.x}
                    y1={model.points.xFace.y}
                    x2={model.points.xFace.callout.connectorX}
                    y2={model.points.xFace.callout.connectorY}
                    stroke={model.points.xFace.color}
                    strokeWidth="1.1"
                    strokeDasharray="3 2"
                  />
                  <rect
                    x={model.points.xFace.callout.boxX}
                    y={model.points.xFace.callout.boxY}
                    width={model.points.xFace.callout.width}
                    height={model.points.xFace.callout.height}
                    rx="4"
                    fill={colors.calloutFill}
                    fillOpacity={colors.calloutFillOpacity}
                    stroke={model.points.xFace.color}
                    strokeOpacity={colors.calloutBorderOpacity}
                  />
                  <text
                    x={model.points.xFace.callout.titleX}
                    y={model.points.xFace.callout.titleY}
                    fontSize={FACE_LABEL_FONT_SIZE}
                    fill={model.points.xFace.color}
                    fontWeight="700"
                    textAnchor={model.points.xFace.callout.textAnchor}
                  >
                    {model.points.xFace.title}
                  </text>
                  <text
                    x={model.points.xFace.callout.detailX}
                    y={model.points.xFace.callout.detailY}
                    fontSize={FACE_LABEL_DETAIL_FONT_SIZE}
                    fill={colors.calloutDetail}
                    fontWeight="600"
                    textAnchor={model.points.xFace.callout.textAnchor}
                  >
                    {model.points.xFace.detail}
                  </text>
                </g>
                <g>
                  <circle cx={model.points.yFace.x} cy={model.points.yFace.y} r="4.2" fill={model.points.yFace.color} />
                  <line
                    x1={model.points.yFace.x}
                    y1={model.points.yFace.y}
                    x2={model.points.yFace.callout.connectorX}
                    y2={model.points.yFace.callout.connectorY}
                    stroke={model.points.yFace.color}
                    strokeWidth="1.1"
                    strokeDasharray="3 2"
                  />
                  <rect
                    x={model.points.yFace.callout.boxX}
                    y={model.points.yFace.callout.boxY}
                    width={model.points.yFace.callout.width}
                    height={model.points.yFace.callout.height}
                    rx="4"
                    fill={colors.calloutFill}
                    fillOpacity={colors.calloutFillOpacity}
                    stroke={model.points.yFace.color}
                    strokeOpacity={colors.calloutBorderOpacity}
                  />
                  <text
                    x={model.points.yFace.callout.titleX}
                    y={model.points.yFace.callout.titleY}
                    fontSize={FACE_LABEL_FONT_SIZE}
                    fill={model.points.yFace.color}
                    fontWeight="700"
                    textAnchor={model.points.yFace.callout.textAnchor}
                  >
                    {model.points.yFace.title}
                  </text>
                  <text
                    x={model.points.yFace.callout.detailX}
                    y={model.points.yFace.callout.detailY}
                    fontSize={FACE_LABEL_DETAIL_FONT_SIZE}
                    fill={colors.calloutDetail}
                    fontWeight="600"
                    textAnchor={model.points.yFace.callout.textAnchor}
                  >
                    {model.points.yFace.detail}
                  </text>
                </g>
                <g>
                  <circle cx={model.points.selected.x} cy={model.points.selected.y} r="4.2" fill={model.points.selected.color} />
                  <text
                    x={model.points.selected.x + 7}
                    y={model.points.selected.y - 8}
                    fontSize="10.5"
                    fill={model.points.selected.color}
                    fontWeight="700"
                  >
                    {model.points.selected.label}
                  </text>
                </g>
                <g>
                  <circle
                    cx={model.points.maxShearNegative.x}
                    cy={model.points.maxShearNegative.y}
                    r="4.2"
                    fill={colors.maxShear}
                  />
                  <SvgTextWithSubscript
                    x={model.points.maxShearNegative.x + 8}
                    y={model.points.maxShearNegative.y - 8}
                    fontSize={10.5}
                    fill={colors.maxShear}
                    fontWeight="400"
                    parts={[{ text: '-τ' }, { text: 'max', subscript: true }]}
                  />
                </g>
                <g>
                  <circle
                    cx={model.points.maxShearPositive.x}
                    cy={model.points.maxShearPositive.y}
                    r="4.2"
                    fill={colors.maxShear}
                  />
                  <SvgTextWithSubscript
                    x={model.points.maxShearPositive.x + 8}
                    y={model.points.maxShearPositive.y + 13}
                    fontSize={10.5}
                    fill={colors.maxShear}
                    fontWeight="400"
                    parts={[{ text: 'τ' }, { text: 'max', subscript: true }]}
                  />
                </g>
                <g>
                  <rect
                    x={model.annotations.summaryTable.x}
                    y={model.annotations.summaryTable.y}
                    width={model.annotations.summaryTable.width}
                    height={
                      SUMMARY_TABLE_HEADER_HEIGHT + model.annotations.summaryTable.rows.length * SUMMARY_TABLE_ROW_HEIGHT + 10
                    }
                    rx="6"
                    fill={colors.summaryFill}
                    fillOpacity={colors.summaryFillOpacity}
                    stroke={colors.summaryBorder}
                  />
                  <text
                    x={model.annotations.summaryTable.x + SUMMARY_TABLE_HORIZONTAL_PAD}
                    y={model.annotations.summaryTable.y + 13}
                    fontSize={SUMMARY_TABLE_HEADER_FONT_SIZE}
                    fill={colors.summaryHeader}
                    fontWeight="700"
                  >
                    Key Values
                  </text>
                  <line
                    x1={model.annotations.summaryTable.x}
                    y1={model.annotations.summaryTable.y + SUMMARY_TABLE_HEADER_HEIGHT}
                    x2={model.annotations.summaryTable.x + model.annotations.summaryTable.width}
                    y2={model.annotations.summaryTable.y + SUMMARY_TABLE_HEADER_HEIGHT}
                    stroke={colors.summaryDivider}
                    strokeWidth="1"
                  />
                  {model.annotations.summaryTable.rows.map((row, index) => {
                    const rowY =
                      model.annotations.summaryTable.y + SUMMARY_TABLE_HEADER_HEIGHT + 14 + index * SUMMARY_TABLE_ROW_HEIGHT;

                    return (
                      <g key={row.id}>
                        <SvgTextWithSubscript
                          x={model.annotations.summaryTable.x + SUMMARY_TABLE_HORIZONTAL_PAD}
                          y={rowY}
                          fontSize={SUMMARY_TABLE_LABEL_FONT_SIZE}
                          fill={colors.summaryLabel}
                          fontWeight="600"
                          parts={row.parts}
                        />
                        <text
                          x={
                            model.annotations.summaryTable.x + model.annotations.summaryTable.width - SUMMARY_TABLE_HORIZONTAL_PAD
                          }
                          y={rowY}
                          fontSize={SUMMARY_TABLE_VALUE_FONT_SIZE}
                          fill={colors.summaryValue}
                          fontWeight="700"
                          textAnchor="end"
                        >
                          {row.value}
                        </text>
                      </g>
                    );
                  })}
                </g>
              </>
            )}

            {!model.isEmpty &&
              model.ticks.sigma.map((tick) => (
                <g key={`sigma-label-${tick}`}>
                  <line
                    x1={model.mapX(tick)}
                    y1={model.plot.bottom}
                    x2={model.mapX(tick)}
                    y2={model.plot.bottom + 4}
                    stroke={colors.tick}
                    strokeWidth="1"
                  />
                  <text x={model.mapX(tick)} y={model.plot.bottom + 16} fontSize="10" textAnchor="middle" fill={colors.tickText}>
                    {formatValue(tick, 1)}
                  </text>
                </g>
              ))}

            {!model.isEmpty &&
              model.ticks.tau.map((tick) => (
                <g key={`tau-label-${tick}`}>
                  <line
                    x1={model.plot.left - 4}
                    y1={model.mapY(tick)}
                    x2={model.plot.left}
                    y2={model.mapY(tick)}
                    stroke={colors.tick}
                    strokeWidth="1"
                  />
                  <text x={model.plot.left - 8} y={model.mapY(tick) + 3} fontSize="10" textAnchor="end" fill={colors.tickText}>
                    {formatValue(tick, 1)}
                  </text>
                </g>
              ))}

            <text
              x={model.plot.right - 4}
              y={model.plot.bottom + 28}
              fontSize="11"
              textAnchor="end"
              fill={colors.axisText}
              fontWeight="700"
            >
              σ (kPa)
            </text>
            <text
              x={model.plot.left - 38}
              y={model.plot.top + 12}
              fontSize="11"
              textAnchor="middle"
              fill={colors.axisText}
              fontWeight="700"
              transform={`rotate(-90 ${model.plot.left - 38} ${model.plot.top + 12})`}
            >
              τ (kPa)
            </text>
            {!model.isEmpty && (
              <>
                <text x={model.plot.left + 6} y={model.plot.top + 14} fontSize="10.5" fill={colors.mutedText} fontWeight="700">
                  (CW)
                </text>
                <text x={model.plot.left + 6} y={model.plot.bottom - 6} fontSize="10.5" fill={colors.mutedText} fontWeight="700">
                  (CCW)
                </text>
              </>
            )}

            {model.isEmpty && (
              <text x={VIEW_WIDTH / 2} y={VIEW_HEIGHT / 2} textAnchor="middle" fontSize="14" fill={colors.emptyText}>
                Enter valid stress inputs to render the Mohr circle.
              </text>
            )}
          </svg>
        </Box>
      </Box>
    </Stack>
  );
};

export default MohrCircleSketch;
